import React, { useState } from "react";
import { ChevronDown, ChevronUp, Sparkles, Trash2 } from "lucide-react";
import { UserPreference } from "../../../../../services/ai/types/ScanHistory.types";
import { generatePreferenceExplanation } from "../../../../../services/ai/PreferenceDetector";

interface PreferenceBannerProps {
  appliedCount: number;
  preferences: UserPreference[];
  onClearPreferences: () => void;
}

export const PreferenceBanner = ({
  appliedCount,
  preferences,
  onClearPreferences
}: PreferenceBannerProps): React.ReactElement | null => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (appliedCount === 0 || preferences.length === 0) {
    return null;
  }

  return (
    <section
      className="my-3 rounded-lg border border-[var(--borderColor)] p-3"
      aria-label="AI preference banner"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <Sparkles
          className="size-4 flex-shrink-0 text-[hsl(var(--info))]"
          aria-hidden="true"
        />
        <span className="flex-1 text-sm font-semibold text-foreground">
          AI Co-Pilot applied {appliedCount} preference
          {appliedCount !== 1 ? "s" : ""} to this scan.
        </span>
        {isExpanded ? (
          <ChevronUp
            className="size-4 flex-shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        ) : (
          <ChevronDown
            className="size-4 flex-shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-2">
          {preferences.map((pref, index) => (
            <div
              key={`${pref.type}-${pref.areaName ?? ""}-${index}`}
              className="rounded-md bg-card p-2.5"
            >
              <p className="text-xs font-semibold text-foreground">
                {pref.description}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {generatePreferenceExplanation(pref)}
              </p>
            </div>
          ))}
          <button
            type="button"
            className="mt-2 flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            onClick={onClearPreferences}
            aria-label="Clear all preferences"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            Clear preferences
          </button>
        </div>
      )}
    </section>
  );
};
